/**
 * WizardSlotsScreen Component (Web Version)
 * Uses v2 availability API with per-service professional assignments
 * Features: Time Grid (1 service) vs Itinerary Cards (2+ services), Brazilian formatting
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Clock, Users, CreditCard, Check } from 'lucide-react';
import { useWizardStore } from '../../stores/wizardStore';
import { useAuthStore } from '../../stores/authStore';
import { getAvailabilityV2, transformV2SlotsForUI } from '../../services/wizardApiService';
import GuestInfoBottomSheet from './GuestInfoBottomSheet';

const WizardSlotsScreen = () => {
  // State for guest info bottom sheet
  const [showGuestInfoSheet, setShowGuestInfoSheet] = useState(false);

  const {
    selectedServices,
    selectedDate,
    serviceAssignments,
    availableProfessionals,
    availableSlots,
    selectedSlot,
    isLoading,
    error,
    setAvailableSlots,
    setSelectedSlot,
    setLoading,
    setError,
    clearError,
    goToNextStep,
    clientData,
  } = useWizardStore();

  // Get auth state to check if user is a guest
  const { isGuestUser } = useAuthStore();

  // Load available slots when component mounts or assignments change
  useEffect(() => {
    loadAvailableSlots();
  }, [selectedDate, serviceAssignments]);

  const loadAvailableSlots = useCallback(async () => {
    // Ensure we have date, services, and all assignments
    const assignmentCount = Object.keys(serviceAssignments || {}).length;
    if (!selectedDate || selectedServices.length === 0 || assignmentCount !== selectedServices.length) {
      return;
    }

    setLoading(true);
    clearError();

    try {
      // Call v2 API with service assignments
      const v2Response = await getAvailabilityV2({
        services: selectedServices,
        assignments: serviceAssignments,
        date: selectedDate,
        slotInterval: 15
      });

      // Transform v2 response to UI-friendly format
      const slots = transformV2SlotsForUI(v2Response, selectedServices, serviceAssignments);
      setAvailableSlots(slots);

    } catch (err) {
      console.error('Error loading v2 availability:', err);
      setError('Erro ao carregar horários disponíveis. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [selectedDate, selectedServices, serviceAssignments, setLoading, clearError, setAvailableSlots, setError]);

  // Handle slot selection (mobile-exact: no auto-advance)
  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    // Mobile behavior: No auto-advance - user must manually click continue button
  };

  // Format time for display (Brazilian 24-hour format)
  const formatTime = (timeString) => {
    try {
      const [hours, minutes] = timeString.split(':');
      return `${hours}:${minutes}`;
    } catch (error) {
      return timeString;
    }
  };

  // Format time for grid (just HH:MM)
  const formatTimeForGrid = (timeString) => {
    return formatTime(timeString);
  };

  // Format duration
  const formatDuration = (minutes) => {
    if (minutes === undefined || minutes === null || minutes <= 0) return '-';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h > 0 ? h + 'h ' : ''}${m > 0 ? m + 'min' : ''}`.trim() || '-';
  };

  // Format price
  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  // Calculate service times for itinerary cards
  // Uses service data from v2 API which includes pre-calculated start_time/end_time
  // Also enriches with variation info from selectedServices
  const calculateServiceTimes = (slot) => {
    if (!slot.services || slot.services.length === 0) return [];

    return slot.services.map((service, index) => {
      // Use timing from v2 API response - backend calculates correct sequential/parallel timing
      const startTime = service.start_time
        ? service.start_time.substring(0, 5) // HH:MM format
        : slot.start_time?.substring(0, 5) || '--:--';

      // Get variation info from selectedServices by matching service_id or originalServiceId or index
      const selectedService = selectedServices.find(s =>
        s.id === service.service_id ||
        s.originalServiceId === service.service_id ||
        s.cartItemId === service.cart_item_id
      ) || selectedServices[index];
      const variationName = selectedService?.selectedVariation?.name;
      const hasVariation = variationName && variationName !== 'Padrão';

      return {
        ...service,
        calculated_start_time: startTime,
        variation_name: hasVariation ? variationName : null,
        display_name: hasVariation
          ? `${service.service_name || service.name} - ${variationName}`
          : (service.service_name || service.name)
      };
    });
  };

  // Get unique professional count
  const getUniqueProfessionalCount = (slot) => {
    if (!slot.services) return 0;
    const uniqueProfessionals = new Set(slot.services.map(s => s.professional_id));
    return uniqueProfessionals.size;
  };

  // Get professional name by ID
  const getProfessionalName = (professionalId) => {
    const professional = availableProfessionals.find(p => p.id === professionalId);
    if (professional) {
      return (professional.full_name || professional.email || '').split(' ')[0];
    }
    return 'Prof.';
  };

  // Get display name for service with variation
  const getServiceDisplayName = (service) => {
    const variation = service.selectedVariation;
    if (variation && variation.name && variation.name !== 'Padrão') {
      return `${service.name} - ${variation.name}`;
    }
    return service.name;
  };

  // Render services summary header with assigned professionals - Luxury Design
  const renderServicesHeader = () => (
    <div className="sticky top-0 bg-brand-light dark:bg-brand-dark z-10 p-4 border-b border-brand-dark/10 dark:border-brand-light/10">
      <div className="flex overflow-x-auto gap-2 scrollbar-hide">
        {selectedServices.map((service, index) => {
          const assignedProfId = serviceAssignments[String(index)];
          const profName = getProfessionalName(assignedProfId);

          return (
            <div
              key={service.cartItemId || service.id || index}
              className="relative flex-shrink-0"
            >
              <div className="bg-brand-light dark:bg-brand-dark border border-brand-dark/20 dark:border-brand-light/20 px-3 py-2 min-w-20">
                <div className="text-center">
                  <div className="text-xs font-medium text-brand-dark dark:text-brand-light leading-tight tracking-wide">
                    {service.name}
                  </div>
                  {service.selectedVariation && service.selectedVariation.name && service.selectedVariation.name !== 'Padrão' && (
                    <div className="text-[10px] text-brand-dark/70 dark:text-brand-light/70 leading-tight tracking-wide mt-0.5">
                      {service.selectedVariation.name}
                    </div>
                  )}
                  <div className="text-[10px] text-brand-dark/50 dark:text-brand-light/50 leading-tight tracking-wider mt-0.5">
                    {formatDuration(service.duration_minutes)}
                  </div>
                  <div className="text-[10px] text-brand-dark/60 dark:text-brand-light/60 font-medium mt-1">
                    {profName}
                  </div>
                </div>
              </div>

              {/* All services are assigned - Square indicator */}
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-600 dark:bg-green-400 flex items-center justify-center">
                <span className="text-white dark:text-black text-xs leading-none">✓</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // Render time grid item for single service - Luxury Design
  const renderTimeGridItem = (slot) => {
    const isSelected = selectedSlot?.id === slot.id;
    const isUnavailable = slot.status === 'unavailable';

    return (
      <button
        key={slot.id}
        onClick={() => !isUnavailable && handleSlotSelect(slot)}
        disabled={isUnavailable}
        className={`
          w-full min-h-12 border transition-all duration-300
          flex items-center justify-center px-2 py-3 mb-2
          ${isSelected
            ? 'bg-brand-dark dark:bg-brand-light border-brand-dark dark:border-brand-light text-brand-light dark:text-brand-dark'
            : isUnavailable
            ? 'bg-brand-light dark:bg-brand-dark border-brand-dark/10 dark:border-brand-light/10 text-brand-dark/30 dark:text-brand-light/30 cursor-not-allowed'
            : 'bg-brand-light dark:bg-brand-dark border-brand-dark/20 dark:border-brand-light/20 text-brand-dark dark:text-brand-light hover:bg-brand-dark/5 dark:hover:bg-brand-light/5'
          }
        `}
      >
        <span className="text-sm font-medium tracking-wide">
          {formatTimeForGrid(slot.start_time)}
        </span>
      </button>
    );
  };

  // Render time grid for single service (3-column grid) - Show all slots
  const renderTimeGrid = () => {
    return (
      <div className="p-4">
        {/* 3-column grid matching mobile - Show ALL slots for single service */}
        <div className="grid grid-cols-3 gap-2">
          {availableSlots.map(slot => renderTimeGridItem(slot))}
        </div>
      </div>
    );
  };

  // Render itinerary card for multiple services - Luxury Design
  const renderItineraryCard = (slot) => {
    const isSelected = selectedSlot?.id === slot.id;
    const servicesWithTimes = calculateServiceTimes(slot);
    const professionalCount = getUniqueProfessionalCount(slot);

    // Calculate total duration from services if not provided
    const totalDuration = slot.total_duration_minutes ||
      selectedServices.reduce((sum, s) => sum + (s.duration_minutes || 0), 0);

    // Calculate total price from services if not provided
    const totalPrice = slot.total_price ||
      selectedServices.reduce((sum, s) => sum + (parseFloat(s.price) || 0), 0);

    // Calculate end time if not provided
    const endTime = slot.end_time || (() => {
      const start = new Date(`1970-01-01T${slot.start_time}`);
      start.setMinutes(start.getMinutes() + totalDuration);
      return start.toTimeString().substring(0, 5);
    })();

    return (
      <div
        key={slot.id}
        onClick={() => handleSlotSelect(slot)}
        className={`
          relative m-4 p-5 border cursor-pointer transition-all duration-300
          ${isSelected
            ? 'bg-brand-dark/5 dark:bg-brand-light/5 border-brand-dark dark:border-brand-light border-2'
            : 'bg-brand-light dark:bg-brand-dark border-brand-dark/20 dark:border-brand-light/20 hover:bg-brand-dark/5 dark:hover:bg-brand-light/5'
          }
        `}
      >
        {/* Header: Time range + duration */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Clock size={16} className={isSelected ? 'text-brand-dark dark:text-brand-light' : 'text-brand-dark/50 dark:text-brand-light/50'} strokeWidth={1.5} />
            <span className={`font-medium tracking-wide ${isSelected ? 'text-brand-dark dark:text-brand-light' : 'text-brand-dark dark:text-brand-light'}`}>
              {formatTime(slot.start_time)} – {formatTime(endTime)}
            </span>
            <span className={`text-sm tracking-wide ${isSelected ? 'text-brand-dark/70 dark:text-brand-light/70' : 'text-brand-dark/60 dark:text-brand-light/60'}`}>
              • Total {formatDuration(totalDuration)}
            </span>
          </div>
        </div>

        {/* Summary: Professionals + Price */}
        <div className="flex items-center justify-between mb-4 pb-4 border-b border-brand-dark/10 dark:border-brand-light/10">
          <div className="flex items-center space-x-2">
            <Users size={14} className={isSelected ? 'text-brand-dark dark:text-brand-light' : 'text-brand-dark/50 dark:text-brand-light/50'} strokeWidth={1.5} />
            <span className={`text-sm tracking-wide ${isSelected ? 'text-brand-dark/70 dark:text-brand-light/70' : 'text-brand-dark/60 dark:text-brand-light/60'}`}>
              {professionalCount} profissional{professionalCount > 1 ? 'is' : ''}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <CreditCard size={14} className={isSelected ? 'text-brand-dark dark:text-brand-light' : 'text-brand-dark/50 dark:text-brand-light/50'} strokeWidth={1.5} />
            <span className={`text-sm font-serif font-bold tracking-wide ${isSelected ? 'text-brand-dark dark:text-brand-light' : 'text-brand-dark dark:text-brand-light'}`}>
              {formatPrice(totalPrice)}
            </span>
          </div>
        </div>

        {/* Services List - Itinerary */}
        <div className="space-y-2">
          {servicesWithTimes.map((service, index) => (
            <div key={index} className="flex items-center justify-between text-sm">
              <div className="flex-1">
                <span className={`font-medium tracking-wide ${isSelected ? 'text-brand-dark dark:text-brand-light' : 'text-brand-dark dark:text-brand-light'}`}>
                  {service.calculated_start_time}
                </span>
                <span className={`ml-2 tracking-wide ${isSelected ? 'text-brand-dark/70 dark:text-brand-light/70' : 'text-brand-dark/70 dark:text-brand-light/70'}`}>
                  {service.service_name || service.name}
                  {service.variation_name && !(service.service_name || service.name || '').includes(service.variation_name) && ` - ${service.variation_name}`}
                </span>
              </div>
              <span className={`text-xs tracking-wide ${isSelected ? 'text-brand-dark/60 dark:text-brand-light/60' : 'text-brand-dark/50 dark:text-brand-light/50'}`}>
                {service.professional_name}
              </span>
            </div>
          ))}
        </div>

        {/* Selection indicator - Square */}
        {isSelected && (
          <div className="absolute -top-1 -right-1 w-4 h-4 bg-brand-dark dark:bg-brand-light flex items-center justify-center">
            <Check size={10} className="text-brand-light dark:text-brand-dark" strokeWidth={2.5} />
          </div>
        )}
      </div>
    );
  };

  // Render itinerary slots list for multiple services - Show all slots
  const renderItinerarySlots = () => {
    return (
      <div>
        {availableSlots.map(slot => renderItineraryCard(slot))}
      </div>
    );
  };

  // Mobile behavior: No success snackbar - only visual selection feedback

  // Render loading state - Luxury Design
  const renderLoadingState = () => (
    <div className="flex flex-col items-center justify-center py-16">
      <div className="w-8 h-8 border-2 border-brand-dark dark:border-brand-light border-t-transparent mx-auto mb-4 animate-spin rounded-full"></div>
      <p className="text-brand-dark/60 dark:text-brand-light/60 text-xs uppercase tracking-wider">Carregando horários disponíveis...</p>
    </div>
  );

  // Render error state - Luxury Design
  const renderErrorState = () => (
    <div className="text-center py-16 px-4">
      <p className="text-red-600 dark:text-red-400 mb-6 tracking-wide">{error}</p>
      <button
        onClick={loadAvailableSlots}
        className="px-6 py-3 bg-brand-dark dark:bg-brand-light text-brand-light dark:text-brand-dark border border-brand-dark dark:border-brand-light font-medium uppercase tracking-widest text-sm hover:bg-brand-dark/90 dark:hover:bg-brand-light/90 transition-all duration-300"
      >
        Tentar novamente
      </button>
    </div>
  );

  // Render empty state - Luxury Design
  const renderEmptyState = () => (
    <div className="text-center py-16 px-4">
      <Clock size={48} className="text-brand-dark/20 dark:text-brand-light/20 mx-auto mb-4" strokeWidth={1.5} />
      <p className="text-brand-dark dark:text-brand-light mb-2 font-serif tracking-wide">Nenhum horário disponível</p>
      <p className="text-sm text-brand-dark/60 dark:text-brand-light/60 tracking-wide">
        Tente selecionar uma data diferente ou outros profissionais
      </p>
    </div>
  );

  // Handle continue button click - show guest info sheet or proceed to confirmation
  const handleContinueClick = () => {
    if (!selectedSlot) return;

    // Check if user is a guest and doesn't have REAL client data yet
    // Placeholder guest data has name="Guest" and no phone - we need to collect real info
    const isGuest = isGuestUser();
    const hasRealClientData = clientData?.id && clientData?.phone && clientData?.name !== 'Guest';

    console.log('[WizardSlotsScreen] handleContinueClick:', { isGuest, hasRealClientData, clientData });

    if (isGuest && !hasRealClientData) {
      // Show guest info bottom sheet to collect real client data
      console.log('[WizardSlotsScreen] Showing GuestInfoBottomSheet');
      setShowGuestInfoSheet(true);
    } else {
      // Regular user or guest with real client data - proceed to confirmation
      console.log('[WizardSlotsScreen] Proceeding to confirmation');
      goToNextStep();
    }
  };

  // Render continue button - Luxury Design
  const renderContinueButton = () => {
    const canContinue = selectedSlot !== null;

    return (
      <div className="fixed bottom-16 left-0 right-0 bg-brand-light dark:bg-brand-dark border-t border-brand-dark/10 dark:border-brand-light/10 p-6">
        <button
          onClick={handleContinueClick}
          disabled={!canContinue}
          className={`w-full py-5 border font-medium uppercase tracking-widest text-sm transition-all duration-300 ${
            canContinue
              ? 'bg-brand-dark dark:bg-brand-light text-brand-light dark:text-brand-dark border-brand-dark dark:border-brand-light hover:bg-brand-dark/90 dark:hover:bg-brand-light/90'
              : 'bg-brand-dark/10 dark:bg-brand-light/10 text-brand-dark/40 dark:text-brand-light/40 border-brand-dark/10 dark:border-brand-light/10 cursor-not-allowed'
          }`}
        >
          {canContinue ? 'Confirmar Horário' : 'Selecione um horário'}
        </button>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col bg-brand-light dark:bg-brand-dark">
      {/* Services Header */}
      {renderServicesHeader()}

      {/* Main Content - Conditional Layout (Mobile-Exact Logic) */}
      <div className="flex-1 overflow-y-auto pb-32 mobile-scroll">
        {isLoading ? (
          renderLoadingState()
        ) : error ? (
          renderErrorState()
        ) : availableSlots.length === 0 ? (
          renderEmptyState()
        ) : (
          // CRITICAL: Mobile-exact conditional logic
          selectedServices.length === 1 ? renderTimeGrid() : renderItinerarySlots()
        )}
      </div>

      {/* Continue Button */}
      {renderContinueButton()}

      {/* Guest Info Bottom Sheet */}
      <GuestInfoBottomSheet
        isVisible={showGuestInfoSheet}
        onClose={() => setShowGuestInfoSheet(false)}
        onSuccess={() => {
          // Guest info submitted successfully, component handles goToNextStep
          setShowGuestInfoSheet(false);
        }}
      />

      {/* Custom Styles */}
      <style>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }

        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default WizardSlotsScreen;